/**
 * Multi-select state machine for the bulk-add discovery selector.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The rules:
 *
 *   - State shape `{ multiSelection: Discovery['id'][], bulkMode: boolean }`
 *     mirrors the source-slice fields the store carries. The reducer
 *     produces transitions that the selector pipes into the producer;
 *     the reducer never directly touches the store.
 *
 *   - `bulkMode` is DERIVED, not stored-as-explicit (ENG-4485): it is
 *     true iff the selection is non-empty. Ticking any checkbox —
 *     including the first/only one — enters bulk-add mode; unchecking
 *     everything is the only exit. This removes the Single/Multiple
 *     selector: WHICH control the user touches decides the mode (a
 *     checkbox → bulk, a row click → single-add), not an explicit
 *     toggle. It is NOT a return to #1228's count threshold
 *     (`length > 1`): the boundary is now ONE (`length > 0`), so a lone
 *     checked camera stays in bulk (creds asked once at resolve-in-place)
 *     instead of bridging into single-add mid-selection — the ENG-4433
 *     fix is preserved. Feature: bulk-add-sources-feature.
 *
 *   - Because `bulkMode` is derived, TOGGLE / SELECT_ALL_UP_TO_CAP /
 *     DESELECT_ALL each set `bulkMode = <next selection>.length > 0`.
 *     There are no explicit ENTER_SELECT_MODE / EXIT_SELECT_MODE actions —
 *     they were removed with the toggle (ENG-4485 §3.6) rather than left
 *     unreachable.
 *
 *   - Filter pills do NOT clear the selection. CLEAR_ON_FILTER is
 *     therefore a deliberate no-op — it lives in the action set so a
 *     future regression that re-introduces filter-clear behavior shows
 *     up as an explicit action change rather than a silent state
 *     mutation.
 *
 *   - SELECT_ALL_UP_TO_CAP enforces the billing cap: if visible
 *     discoveries exceed `remainingCameraSlots`, the reducer selects up
 *     to the cap (NOT all-then-trim, NOT none-and-error).
 *
 *   - REPLACE swaps a selected camera's transport in place (a camera
 *     advertised over several protocols is one row and one selection), so
 *     the selection size never grows on a transport switch.
 *
 *   - CHECK_DISABLED short-circuits TOGGLE for ids the caller has
 *     marked disabled (Iris-enabled rows, billing-cap, no-agentId).
 *     The reducer does not own the disabled-set computation — selector
 *     owns derivation, reducer owns enforcement.
 *
 * Per `think-in-systems` (ai.knowledge/global): extracting this reducer
 * out of `selector.tsx` lets the state-machine contract be tested in
 * isolation (snapshot pattern, no React render required). Keeping the
 * store field named `bulkMode` (rather than renaming to `selectMode`)
 * keeps the ~8 downstream consumers unchanged — minimal coupling.
 *
 * Per `tests-require-user-story-comment` rule: the sibling
 * `use-multi-select-state.test.ts` carries one user-story comment per
 * action branch.
 */

import { useCallback, useState } from 'react';
import type { Discovery } from '#~/device/discovery/index.js';

/** Mutable state shape — mirrors `State.source.multiSelection` + `bulkMode`. */
export interface MultiSelectState {
  multiSelection: Discovery['id'][];
  bulkMode: boolean;
}

/** Initial state — empty selection; derived `bulkMode` is false at 0. */
export const INITIAL_STATE: MultiSelectState = {
  multiSelection: [],
  bulkMode: false
};

/**
 * Action union for the multi-select reducer.
 *
 * One action per user intent. Action types are string literals so
 * transitions show up as string keys in snapshot tests (matches
 * morph-state.ts pattern).
 */
export type MultiSelectAction =
  | { type: 'TOGGLE'; id: Discovery['id']; disabled?: boolean }
  | {
      type: 'SELECT_ALL_UP_TO_CAP';
      // Visible (filtered + non-disabled) discovery ids in display order.
      visibleIds: Discovery['id'][];
      // Already-disabled ids (caller-derived); reducer skips these.
      disabledIds?: Set<Discovery['id']>;
      // Billing cap remaining; reducer trims to this count.
      cap: number;
    }
  | { type: 'DESELECT_ALL' }
  // Swap one camera's selected transport for another in place.
  | { type: 'REPLACE'; from: Discovery['id']; to: Discovery['id'] }
  // Filter pill change does NOT clear the selection. The action exists
  // for explicit no-op semantics — a future refactor that wants to
  // re-introduce filter-clear behavior must change THIS case, which
  // immediately surfaces in the snapshot tests.
  | { type: 'CLEAR_ON_FILTER' };

/**
 * Pure reducer — produces the next state from (state, action).
 *
 * `bulkMode` is DERIVED from the resulting selection length (ENG-4485):
 * every action that mutates `multiSelection` recomputes
 * `bulkMode = next.length > 0`, so the mode can never drift out of sync
 * with the selection.
 *
 * Exported so the snapshot tests can drive transitions without spinning
 * up a React render tree. `useMultiSelectState` (below) wraps this in
 * `useState` + `useCallback` for component consumption.
 *
 * Per `think-in-systems` (ai.knowledge/global): pure function, no side
 * effects, no store access — easy to reason about and trivial to test.
 */
export const reduce = (state: MultiSelectState, action: MultiSelectAction): MultiSelectState => {
  switch (action.type) {
    case 'TOGGLE': {
      // CHECK_DISABLED gate: disabled rows never enter the selection
      // (Iris-enabled, billing-cap exceeded, or unresolvable bridge).
      // Selector owns derivation; reducer owns enforcement.
      if (action.disabled === true) return state;

      const isSelected = state.multiSelection.includes(action.id);
      const next = isSelected
        ? state.multiSelection.filter((id) => id !== action.id)
        : [...state.multiSelection, action.id];

      return {
        multiSelection: next,
        // Derived mode (ENG-4485 §3.3/§3.6): ticking any checkbox —
        // including the first/only one — enters bulk; unchecking the last
        // exits. The boundary is `length > 0` (NOT #1228's `length > 1`),
        // so a lone checked camera stays in bulk with no mid-selection
        // single-add bridge. Per `reference-rules-in-comments`.
        bulkMode: next.length > 0
      };
    }

    case 'SELECT_ALL_UP_TO_CAP': {
      const { visibleIds, disabledIds, cap } = action;

      // Drop disabled ids from the candidate pool first — Iris/billing/
      // no-agentId rows can never enter the selection regardless of
      // select-all intent.
      const eligible = disabledIds ? visibleIds.filter((id) => !disabledIds.has(id)) : visibleIds;

      // Trim to billing cap. NOT all-then-trim (which would briefly
      // overflow the cap and confuse downstream observers); NOT
      // none-and-error (which surprises users at the boundary).
      const trimmed = eligible.slice(0, Math.max(0, cap));

      return {
        multiSelection: trimmed,
        // Derived: select-all enters bulk iff it selected at least one
        // row (ENG-4485). Per `reference-rules-in-comments`.
        bulkMode: trimmed.length > 0
      };
    }

    case 'DESELECT_ALL': {
      return {
        multiSelection: [],
        // Derived: an empty selection IS single-add mode, so unchecking
        // select-all clears everything and thereby exits bulk — the only
        // exit is an empty selection (ENG-4485 §3.5/§6.1). Per
        // `reference-rules-in-comments` (ai.knowledge/global).
        bulkMode: false
      };
    }

    case 'REPLACE': {
      // A camera listed once may switch transport while selected: the new
      // member takes the old one's slot, so the count (and the plan-limit
      // math built on it) never grows. Unselected `from` is a no-op.
      if (action.from === action.to || !state.multiSelection.includes(action.from)) {
        return state;
      }

      const next = state.multiSelection.flatMap((id) => {
        if (id === action.from) return [action.to];
        return id === action.to ? [] : [id];
      });

      return { multiSelection: next, bulkMode: next.length > 0 };
    }

    case 'CLEAR_ON_FILTER': {
      // Deliberate no-op — filter pills hide rows but selection persists
      // across filter changes. See header comment for rationale.
      return state;
    }

    default:
      // Exhaustiveness — TS narrows `action` to `never` here. If a new
      // action type is added without a case, the compiler complains.
      return state;
  }
};

/**
 * The state + bound dispatchers returned by `useMultiSelectState`.
 *
 * Exported so `content.tsx` (which owns the hook so the controller
 * outlives a `<Selector>` remount) can pass the controller down to
 * `<Selector>` as a typed prop without `<Selector>` re-calling the hook
 * (a second instance would fork the reducer state). Per `think-in-systems`
 * (ai.knowledge/global).
 */
export interface MultiSelectController {
  state: MultiSelectState;
  toggle: (id: Discovery['id'], disabled?: boolean) => void;
  selectAllUpToCap: (
    visibleIds: Discovery['id'][],
    disabledIds: Set<Discovery['id']> | undefined,
    cap: number
  ) => void;
  deselectAll: () => void;
  /** Swap a selected discovery for another transport of the same camera. */
  replace: (from: Discovery['id'], to: Discovery['id']) => void;
  clearOnFilter: () => void;
}

/**
 * Hook returning the current state and bound dispatchers for the
 * multi-select machine.
 *
 * Selector consumes the dispatchers, reflects state changes through the
 * Zustand store via the producer, and emits live-region announcements
 * via debounce on the selection length. `content.tsx` owns the hook so
 * the reducer state lives above the `<Selector>` subtree and survives a
 * Selector remount; there is exactly one reducer instance.
 */
export const useMultiSelectState = (): MultiSelectController => {
  const [state, setState] = useState<MultiSelectState>(INITIAL_STATE);

  const toggle = useCallback((id: Discovery['id'], disabled?: boolean): void => {
    // Per `exactOptionalPropertyTypes: true` (root tsconfig): only include
    // `disabled` when defined — passing `undefined` widens the action type.
    setState((prev) =>
      reduce(
        prev,
        disabled === undefined ? { type: 'TOGGLE', id } : { type: 'TOGGLE', id, disabled }
      )
    );
  }, []);

  const selectAllUpToCap = useCallback(
    (
      visibleIds: Discovery['id'][],
      disabledIds: Set<Discovery['id']> | undefined,
      cap: number
    ): void => {
      // Same exactOptionalPropertyTypes guard: omit `disabledIds` key when undefined.
      setState((prev) =>
        reduce(
          prev,
          disabledIds === undefined
            ? { type: 'SELECT_ALL_UP_TO_CAP', visibleIds, cap }
            : { type: 'SELECT_ALL_UP_TO_CAP', visibleIds, disabledIds, cap }
        )
      );
    },
    []
  );

  const deselectAll = useCallback((): void => {
    setState((prev) => reduce(prev, { type: 'DESELECT_ALL' }));
  }, []);

  const replace = useCallback((from: Discovery['id'], to: Discovery['id']): void => {
    setState((prev) => reduce(prev, { type: 'REPLACE', from, to }));
  }, []);

  const clearOnFilter = useCallback((): void => {
    setState((prev) => reduce(prev, { type: 'CLEAR_ON_FILTER' }));
  }, []);

  return {
    state,
    toggle,
    selectAllUpToCap,
    deselectAll,
    replace,
    clearOnFilter
  };
};
